import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Target, GitBranch, Search, AlertCircle, Users, Network, BarChart3 } from 'lucide-react';
import { firlApi } from '@/services/api.js';
import { Card, CardHeader, StatCard, QuickAction, EmptyState, ErrorPanel } from '@/components/common/Card.jsx';
import StatusBadge from '@/components/common/StatusBadge.jsx';
import FirlComplianceBanner from '@/components/firl/FirlComplianceBanner.jsx';
import SeverityLadderBadge from '@/components/firl/SeverityLadderBadge.jsx';
import { fmtRelative } from '@/utils/format.js';

export default function FirlHome() {
  const { t } = useTranslation();
  const ffosQuery = useQuery({
    queryKey: ['firl', 'ffos'],
    queryFn:  () => firlApi.listFfos({ per_page: 6 }),
    retry: 1,
  });

  const anomQuery = useQuery({
    queryKey: ['firl', 'anomalies', 'open'],
    queryFn:  () => firlApi.listAnomalies({ status: 'open', per_page: 6 }),
    refetchInterval: 30000,
    retry: 1,
  });

  const ffos      = ffosQuery.data?.data  ?? [];
  const anomalies = anomQuery.data?.data  ?? [];

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{t('home.firlHome.firlAdminDashboard')}</h1>
        <p className="mt-1.5 text-sm text-slate-600">{t('home.firlHome.fundsFlowObjectsFfosGateEvaluation')}</p>
      </div>

      <FirlComplianceBanner />


      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <StatCard label={t('home.firlHome.activeFfos')}        value={ffosQuery.isLoading ? '…' : ffos.length}      hint={t('home.firlHome.mostRecent6')}          accent="emerald" />
        <StatCard label={t('home.firlHome.openAnomalies')}     value={anomQuery.isLoading ? '…' : anomalies.length} hint={t('home.firlHome.awaitingProgramOfficer')} accent="amber" />
        <StatCard label={t('home.firlHome.enforcementQueue')}  value="—"                                            hint={t('home.firlHome.pendingReview')}          accent="rose" />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
        <QuickAction to="/firl/ffos"        title={t('home.firlHome.ffos')}          description={t('home.firlHome.declareAndVersionFundsFlowObjects')} icon={Target} />
        <QuickAction to="/firl/gates"       title={t('home.firlHome.gateMonitor')}  description={t('home.firlHome.runAndInspectGateEvaluations')}        icon={GitBranch} />
        <QuickAction to="/firl/anomalies"   title={t('home.firlHome.anomalies')}     description={t('home.firlHome.reviewPatternAnomaliesHumansDecide')} icon={Search} />
        <QuickAction to="/firl/enforcement" title={t('home.firlHome.enforcement')}   description={t('home.firlHome.signedEnforcementActionsAndRationale')} icon={AlertCircle} />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-10">
        <QuickAction to="/firl/recipients" title={t('home.firlHome.recipientsRio')}  description={t('home.firlHome.layer3RecipientIdentityOwnershipEligibility')} icon={Users} />
        <QuickAction to="/firl/authority"  title={t('home.firlHome.authorityChain')}   description={t('home.firlHome.visualizeTheCanonical5StageAuthority')}  icon={Network} />
        <QuickAction to="/firl/severity"   title={t('home.firlHome.severityLadder')}   description={t('home.firlHome.l0L1L2L3DistributionAcross')} icon={BarChart3} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('home.firlHome.recentFfos')} description={t('home.firlHome.mostRecentFederalFundObjectives')} />
          <div className="mt-5">
            {ffosQuery.isLoading ? (
              <SkeletonList rows={5} />
            ) : ffosQuery.isError ? (
              <ErrorPanel error={ffosQuery.error} onRetry={ffosQuery.refetch} />
            ) : ffos.length === 0 ? (
              <EmptyState title={t('home.firlHome.noFfosDeclared')} description={t('home.firlHome.declareYourFirstFederalFundObjective')} />
            ) : (
              <ul className="divide-y divide-slate-100">
                {ffos.map(f => (
                  <li key={f.id} className="flex items-center gap-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {f.title || f.objective_code || f.recipient_entity_id || 'FFO'}
                      </div>
                      <div className="text-xs text-slate-500">
                        {f.lifecycle_state ? <span className="font-mono">{f.lifecycle_state}</span> : null}
                        {f.lifecycle_state && ' · '}
                        {fmtRelative(f.created_at)}
                      </div>
                    </div>
                    <StatusBadge status={f.integrity_status || f.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title={t('home.firlHome.openAnomalies')} description={t('home.firlHome.patternAnomaliesAwaitingProgramOfficerReview')} />
          <div className="mt-5">
            {anomQuery.isLoading ? (
              <SkeletonList rows={5} />
            ) : anomQuery.isError ? (
              <ErrorPanel error={anomQuery.error} onRetry={anomQuery.refetch} />
            ) : anomalies.length === 0 ? (
              <EmptyState title={t('home.firlHome.noOpenAnomalies')} description={t('home.firlHome.detectedAnomaliesWillAppearHereFor')} />
            ) : (
              <ul className="divide-y divide-slate-100">
                {anomalies.map(a => (
                  <li key={a.id} className="flex items-center gap-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-900">{a.summary || a.description || a.red_flag_type || 'Anomaly'}</div>
                      <div className="text-xs text-slate-500">{fmtRelative(a.created_at)}</div>
                    </div>
                    {a.severity && <SeverityLadderBadge severity={a.severity} short />}
                    <StatusBadge status={a.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}

function SkeletonList({ rows = 3 }) {
  return (
    <ul className="divide-y divide-slate-100">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="flex items-center gap-4 py-3">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-1/2 rounded-full bg-slate-200 animate-pulse" />
            <div className="h-2.5 w-1/3 rounded-full bg-slate-100 animate-pulse" />
          </div>
          <div className="h-5 w-16 rounded-full bg-slate-100 animate-pulse" />
        </li>
      ))}
    </ul>
  );
}
